// Inline + paragraph + list rendering primitives shared across block renderers.

import type { InlineNode, ListItemNode, ParagraphNode } from "../types";
import { footnoteBodyId, footnoteRefId } from "../footnoteAnchors";

export type RenderFormat = "html" | "markdown";

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const escapeMd = (s: string): string => s.replace(/([\\`*_{}[\]()#+\-.!>|])/g, "\\$1");

/**
 * URL scheme allow-list for any link href rendered into HTML or Markdown from a
 * BriefDocument. Blocks `javascript:`, `data:`, `vbscript:`, and similar
 * script-bearing schemes. Anything that doesn't match falls back to "#".
 */
const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:|#|\/)/i;
export const safeUrl = (href: string | null | undefined): string => {
  const trimmed = (href ?? "").trim();
  if (!trimmed) return "#";
  return SAFE_URL_RE.test(trimmed) ? trimmed : "#";
};

/**
 * Pre-resolved footnote number lookup threaded through inline rendering. Built
 * once in a single top-level walk so that the html + markdown passes (which
 * call renderInline twice over the same node) produce identical numbering and
 * don't double-push into the trailing notes block. When omitted (unit tests),
 * inline refs render as "[?]" placeholders so the renderer never crashes.
 */
export type FootnoteState = {
  numberById: Map<string, number>;
};

const renderInlineFootnote = (
  node: { type: "excerptus.footnoteRef" | "excerptus.endnoteRef"; attrs: { id: string; body: string } },
  fmt: RenderFormat,
  state: FootnoteState | undefined,
): string => {
  const number = state?.numberById.get(node.attrs.id);
  if (!number) return fmt === "html" ? "<sup>[?]</sup>" : "[^?]";
  return fmt === "html"
    ? `<sup class="footnote-ref"><a id="${footnoteRefId(node.attrs.id)}" href="#${footnoteBodyId(node.attrs.id)}">[${number}]</a></sup>`
    : `[^${number}]`;
};

export const renderInline = (
  nodes: InlineNode[] | undefined,
  fmt: RenderFormat,
  state?: FootnoteState,
): string => {
  if (!nodes) return "";
  return nodes
    .map((n) => {
      if (n.type === "hardBreak") return fmt === "html" ? "<br />" : "  \n";
      if (n.type === "excerptus.footnoteRef" || n.type === "excerptus.endnoteRef") {
        return renderInlineFootnote(n, fmt, state);
      }
      const text = fmt === "html" ? escapeHtml(n.text) : escapeMd(n.text);
      let out = text;
      const marks = n.marks ?? [];
      for (const m of marks) {
        if (m.type === "bold") out = fmt === "html" ? `<strong>${out}</strong>` : `**${out}**`;
        else if (m.type === "italic") out = fmt === "html" ? `<em>${out}</em>` : `*${out}*`;
        else if (m.type === "link") {
          const href = safeUrl(m.attrs.href);
          out =
            fmt === "html"
              ? `<a href="${escapeHtml(href)}" rel="noopener">${out}</a>`
              : `[${out}](${href})`;
        }
      }
      return out;
    })
    .join("");
};

export const renderParagraph = (node: ParagraphNode, fmt: RenderFormat, state?: FootnoteState): string => {
  const inner = renderInline(node.content, fmt, state);
  if (fmt === "html") return `<p>${inner}</p>`;
  return inner;
};

export const renderListItems = (
  items: ListItemNode[] | undefined,
  fmt: RenderFormat,
  marker: (i: number) => string,
  state?: FootnoteState,
): string => {
  if (!items) return "";
  return items
    .map((li, i) => {
      const inner = (li.content ?? [])
        .map((p) => renderInline(p.content, fmt, state))
        .join(fmt === "html" ? "" : " ");
      return fmt === "html" ? `<li>${inner}</li>` : `${marker(i)} ${inner}`;
    })
    .join(fmt === "html" ? "" : "\n");
};
